import { useEffect, useState, type ReactNode } from 'react';
import { X, MapPin, Users, Shield } from 'lucide-react';
import type { Match, MatchDetail, Goal, Booking, Substitution } from '@/lib/matches';
import { formatMatchDateTime, stageLabel, formatMatchFullDate } from '@/lib/matches';
import { fetchMatches } from '@/lib/matchesCache';

interface MatchDetailModalProps {
  match: Match | null;
  onClose: () => void;
  useUTC?: boolean;
}

function GoalRow({ goal, isHome }: { goal: Goal; isHome: boolean }) {
  const scorerName = goal.scorer?.name || 'Unknown';
  const hasAssist = goal.assist && goal.assist.name;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-text-muted w-6 text-right tabular-nums shrink-0">
        {goal.minute ?? '?'}{goal.injuryTime ? `+${goal.injuryTime}` : ''}
      </span>
      <span className={`font-medium ${isHome ? '' : 'ml-auto text-right'}`}>
        {scorerName}
      </span>
      {hasAssist && <span className="text-text-muted shrink-0">({goal.assist!.name})</span>}
      <span className="text-[10px] px-1 py-0.5 rounded bg-bg-hover text-text-muted shrink-0">{goal.type || 'REGULAR'}</span>
    </div>
  );
}

function BookingRow({ booking }: { booking: Booking }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-text-muted tabular-nums shrink-0">{booking.minute ?? '?'}'</span>
      <span className={`w-2 h-2.5 rounded-sm shrink-0 ${booking.card === 'YELLOW' ? 'bg-yellow-400' : 'bg-red-500'}`} />
      <span>{booking.player?.name || 'Unknown'}</span>
      <span className="text-text-muted ml-auto">{booking.team?.name || ''}</span>
    </div>
  );
}

function SubstitutionRow({ sub }: { sub: Substitution }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-text-muted tabular-nums shrink-0">{sub.minute ?? '?'}'</span>
      <span className="text-success shrink-0">↑ {sub.playerIn?.name || 'Unknown'}</span>
      <span className="text-error shrink-0">↓ {sub.playerOut?.name || 'Unknown'}</span>
    </div>
  );
}

export function MatchDetailModal({ match, onClose, useUTC }: MatchDetailModalProps) {
  const [detail, setDetail] = useState<MatchDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!match) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchMatches({ id: match.id })
      .then((data: unknown) => {
        if (cancelled) return;
        const d = data as { match?: MatchDetail };
        setDetail(d?.match || (data as MatchDetail) || null);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load match details');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [match]);

  if (!match) return null;

  const homeCrest = match.homeTeam?.crest;
  const awayCrest = match.awayTeam?.crest;
  const homeScore = match.score?.fullTime?.home;
  const awayScore = match.score?.fullTime?.away;
  const hasScore = homeScore !== null && homeScore !== undefined && awayScore !== null && awayScore !== undefined;

  const venue: string | undefined = detail?.venue;
  const attendance = detail?.attendance;
  const referees = detail?.referees || [];
  const goals = detail?.goals || [];
  const bookings = detail?.bookings || [];
  const substitutions = detail?.substitutions || [];
  const odds = detail?.odds;

  const content: ReactNode = loading ? (
    <div className="flex items-center justify-center py-6">
      <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
    </div>
  ) : error ? (
    <p className="text-xs text-error text-center py-3">{error}</p>
  ) : (
    <>
      {(venue || attendance) && (
        <div className="border border-border rounded-lg p-3 mb-3">
          <h4 className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Venue
          </h4>
          {venue && <p className="text-sm">{venue}</p>}
          {attendance != null && <p className="text-xs text-text-muted">Attendance: {attendance.toLocaleString()}</p>}
        </div>
      )}

      {referees.length > 0 && (
        <div className="border border-border rounded-lg p-3 mb-3">
          <h4 className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1">
            <Shield className="w-3 h-3" /> Officials
          </h4>
          <div className="space-y-1">
            {referees.map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-text-muted">{r.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Official'}</span>
                <span>{r.name || 'Unknown'} {r.nationality ? `(${r.nationality})` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {goals.length > 0 && (
        <div className="border border-border rounded-lg p-3 mb-3">
          <h4 className="text-xs font-semibold text-text-muted mb-2">Goals</h4>
          <div className="space-y-1.5">
            {goals.map((g, i) => (
              <GoalRow key={i} goal={g} isHome={g.team?.name === match.homeTeam?.name} />
            ))}
          </div>
        </div>
      )}

      {bookings.length > 0 && (
        <div className="border border-border rounded-lg p-3 mb-3">
          <h4 className="text-xs font-semibold text-text-muted mb-2">Bookings</h4>
          <div className="space-y-1">
            {bookings.map((b, i) => (
              <BookingRow key={i} booking={b} />
            ))}
          </div>
        </div>
      )}

      {substitutions.length > 0 && (
        <div className="border border-border rounded-lg p-3 mb-3">
          <h4 className="text-xs font-semibold text-text-muted mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" /> Substitutions
          </h4>
          <div className="space-y-1">
            {substitutions.map((s, i) => (
              <SubstitutionRow key={i} sub={s} />
            ))}
          </div>
        </div>
      )}

      {odds && (
        <div className="border border-border rounded-lg p-3">
          <h4 className="text-xs font-semibold text-text-muted mb-2">Odds</h4>
          <div className="flex gap-2 text-xs">
            <div className="flex-1 bg-bg-hover rounded p-2 text-center">
              <div className="text-text-muted">Home</div>
              <div className="font-semibold">{odds.homeWin?.toFixed(2) ?? '-'}</div>
            </div>
            <div className="flex-1 bg-bg-hover rounded p-2 text-center">
              <div className="text-text-muted">Draw</div>
              <div className="font-semibold">{odds.draw?.toFixed(2) ?? '-'}</div>
            </div>
            <div className="flex-1 bg-bg-hover rounded p-2 text-center">
              <div className="text-text-muted">Away</div>
              <div className="font-semibold">{odds.awayWin?.toFixed(2) ?? '-'}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-bg-elevated border border-border rounded-xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-bg-elevated border-b border-border flex items-center justify-between px-4 py-3 z-10">
          <span className="text-xs text-text-muted">
            {formatMatchFullDate(match.utcDate, !!useUTC)} · {formatMatchDateTime(match.utcDate, !!useUTC)}
          </span>
          <button onClick={onClose} className="p-1 hover:bg-bg-hover rounded-md transition-colors">
            <X className="w-4 h-4 text-text-muted" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="flex flex-col items-center gap-1 flex-1 text-right">
              {homeCrest && <img src={homeCrest} alt="" className="w-10 h-10 object-contain" />}
              <span className="text-sm font-semibold">{match.homeTeam?.shortName || match.homeTeam?.name || 'Home'}</span>
            </div>
            <div className="text-center flex-shrink-0">
              {hasScore ? (
                <span className="text-3xl font-bold tabular-nums">
                  {homeScore}-{awayScore}
                </span>
              ) : (
                <span className="text-xs text-text-muted">vs</span>
              )}
              <div className="text-[10px] text-text-muted mt-0.5 uppercase">
                {stageLabel(match.stage || '')}
              </div>
            </div>
            <div className="flex flex-col items-center gap-1 flex-1 text-left">
              {awayCrest && <img src={awayCrest} alt="" className="w-10 h-10 object-contain" />}
              <span className="text-sm font-semibold">{match.awayTeam?.shortName || match.awayTeam?.name || 'Away'}</span>
            </div>
          </div>

          <div className="text-center mb-4 text-xs text-text-muted">
            {match.competition?.emblem && (
              <img src={match.competition.emblem} alt="" className="w-4 h-4 object-contain inline-block mr-1" />
            )}
            {match.competition?.name || ''}
            {match.group && <span> · Group {match.group}</span>}
          </div>

          {content}
        </div>
      </div>
    </div>
  );
}
